import React, { useCallback, useMemo, useState } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { fetchAllDropdownListsStarted } from '../../../admin/dropdown-lists/store';
import { fetchAllEntitiesStarted } from '../../../admin/entity/store';
import { IncompleteDetailsModal } from '../../../shared/modal/IncompleteDetailsModal';
import { OpinionDetails } from './OpinionDetails';
import { fetchOpinionsStarted, getIncompleteOpinion, getIncompleteOpinionSelectedPage, getIncompleteOpinionUrl, getIsOpening, getIsUpdatingOpinion, getOpinionDetailsModalOpen, getOpinionDetailsUpdated, getSelectedIncompleteOpinions, setIncompleteOpinionSelectedPage, skipIncompleteOpinion, toggleOpinionDetailsModal, updateOpinionDetailsStarted } from '../store';
import { fetchAvailableDocumentNamesStarted } from '../../../admin/documents/store';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { OpinionCommissionedBy } from '../../../admin/opinions/store';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { OpinionConfirmationModal } from './OpinionConfirmationModal';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { Position } from '../../../shared/modal/PositionModal';
import { DocumentViewerType } from '../../../documents/document-viewer/store';

const HEIGHT_OFFSET = 250;
const TOP_OFFSET = 156;
const MODAL_WIDTH_PERCENTAGE = 0.9;
const MODAL_WIDTH_PERCENTAGE_OFFSET = 0.05;
const DETAILS_WIDTH_PERCENTAGE = 0.4;
const DOCUMENT_PREVIEW_WIDTH_PERCENTAGE = 0.6;
const DOCUMENT_PREVIEW_MARGIN = 10;

export const OpinionDetailsModal: React.FC = () => {
    const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const opinion = useAppSelector(getIncompleteOpinion);
    const isOpening = useAppSelector(getIsOpening);
    const opinionUrl = useAppSelector(getIncompleteOpinionUrl);
    const opinionDetailsModalOpen = useAppSelector(getOpinionDetailsModalOpen);
    const selectedIncompleteOpinions = useAppSelector(getSelectedIncompleteOpinions);
    const opinionDetailsUpdated = useAppSelector(getOpinionDetailsUpdated);
    const isUpdatingOpinionDetails = useAppSelector(getIsUpdatingOpinion);
    const selectedPage = useAppSelector(getIncompleteOpinionSelectedPage);
    const [showDocumentPreview, setShowDocumentPreview] = useState<boolean>(true);

    const [screenWidth, screenHeight] = useWindowResize();

    const openConfirmationModal = useCallback(() => setConfirmationModalOpen(true), []);
    const closeConfirmationModal = useCallback(() => setConfirmationModalOpen(false), []);
    const closeModal = useCallback(() => dispatch(toggleOpinionDetailsModal(false)), [dispatch]);
    const completeUpload = useCallback((completeWithAI = false) => {
        dispatch(updateOpinionDetailsStarted(completeWithAI));
        closeConfirmationModal();
    }, [dispatch, closeConfirmationModal]);
    const updateAndSkip = useCallback(() => opinionDetailsUpdated ? completeUpload() : dispatch(skipIncompleteOpinion()), [opinionDetailsUpdated, completeUpload, dispatch]);
    const toggleDocumentPreview = useCallback(() => setShowDocumentPreview(!showDocumentPreview), [showDocumentPreview]);

    const setSelectedPage = useCallback((page: number | null) => dispatch(setIncompleteOpinionSelectedPage(page)), [dispatch]);

    useFetchStarted([
        fetchAllDropdownListsStarted(),
        fetchAllEntitiesStarted(),
        fetchAvailableDocumentNamesStarted(),
        fetchOpinionsStarted()
    ]);

    const mimeType = opinion && opinion.mimeType;
    const missingDescription = !isNull(opinion) && !opinion.description.length;
    const missingJurisdiction = !isNull(opinion) && !opinion.jurisdiction;
    const missingOpinionDate = !isNull(opinion) && !opinion.dateOfOpinion;
    const missingOpinionScope = !isNull(opinion) && !opinion.scope;
    const missingOpinionType = !isNull(opinion) && !opinion.type;
    const missingCommissionedBy = !isNull(opinion) && !opinion.commissionedBy;
    const missingCommissionedByIfOther = !isNull(opinion) && opinion.commissionedBy === OpinionCommissionedBy.OTHER && !opinion.commissionedByIfOther;
    const missingSubjectOfOpinion = !isNull(opinion) && opinion?.bespoke === 1 && !opinion.subjectOfOpinion;
    const completeDisabled = missingDescription || missingJurisdiction || missingOpinionDate || missingOpinionScope || missingOpinionType || missingCommissionedBy || isUpdatingOpinionDetails || missingSubjectOfOpinion;

    const completeDisabledTooltip = useMemo(() => {
        let disabledArray = [];
        if (missingDescription) {
            disabledArray.push('Your opinion requires a description');
        }
        if (missingJurisdiction) {
            disabledArray.push('Your opinion requires a jurisdiction');
        }
        if (missingOpinionDate) {
            disabledArray.push('Your opinion requires a date of opinion');
        }
        if (missingOpinionScope) {
            disabledArray.push('Your opinion requires an opinion scope');
        }
        if (missingOpinionType) {
            disabledArray.push('Your opinion requires a type');
        }
        if (missingSubjectOfOpinion) {
            disabledArray.push('Your opinion requires a subject');
        }
        if (missingCommissionedBy || missingCommissionedByIfOther) {
            disabledArray.push('Your opinion requires a commissioner');
        }
        return disabledArray;
    }, [
        missingDescription,
        missingJurisdiction,
        missingOpinionDate,
        missingOpinionScope,
        missingOpinionType,
        missingCommissionedBy,
        missingCommissionedByIfOther,
        missingSubjectOfOpinion
    ]);

    const updateAndSkipLabel = useMemo(() => {
        if (opinionDetailsUpdated) {
            return selectedIncompleteOpinions.length < 2 ? 'Update & Close' : 'Update & Skip';
        }
        return 'Skip';
    }, [opinionDetailsUpdated, selectedIncompleteOpinions]);

    const updateAndSkipDisabled = (selectedIncompleteOpinions.length < 2 && !opinionDetailsUpdated) || !completeDisabled || isUpdatingOpinionDetails;
    const updateAndSkipDisabledTooltip = useMemo(() => {
        let disabledArray = [];
        if (selectedIncompleteOpinions.length < 2 && !opinionDetailsUpdated) {
            disabledArray.push('This is the only remaining opinion that you recently uploaded');
        }
        if (!completeDisabled && !isOpening) {
            disabledArray.push('Opinion is complete');
        }
        return disabledArray;
    }, [opinionDetailsUpdated, selectedIncompleteOpinions, completeDisabled, isOpening]);

    const confirmationModalPosition: Position = useMemo(() => ({
        x: (screenWidth * MODAL_WIDTH_PERCENTAGE_OFFSET) + (screenWidth * (DOCUMENT_PREVIEW_WIDTH_PERCENTAGE * MODAL_WIDTH_PERCENTAGE)) + DOCUMENT_PREVIEW_MARGIN,
        y: TOP_OFFSET
    }), [screenWidth]);
    const confirmationModalHeight = useMemo(() => `${screenHeight - HEIGHT_OFFSET}px`, [screenHeight]);
    const confirmationModalWidth = useMemo(() => `${(screenWidth * (MODAL_WIDTH_PERCENTAGE * DETAILS_WIDTH_PERCENTAGE)) - DOCUMENT_PREVIEW_MARGIN}px`, [screenWidth]);

    const showSkip = useMemo(() => (isUndefined(opinion?.additionalScopes) || opinion?.additionalScopes.length === 0) && (isUndefined(opinion?.sourceOpinionIds) || opinion?.sourceOpinionIds.length === 0), [opinion]);

    return (
        <>
            <IncompleteDetailsModal
                isOpen={opinionDetailsModalOpen}
                closeModal={closeModal}
                completeDisabledTooltip={completeDisabledTooltip}
                completeUpload={openConfirmationModal}
                completeDisabled={completeDisabled}
                updateAndSkipDisabled={updateAndSkipDisabled}
                updateAndSkip={updateAndSkip}
                updateAndSkipDisabledTooltip={updateAndSkipDisabledTooltip}
                updateAndSkipLabel={updateAndSkipLabel}
                url={opinionUrl}
                isOpeningDocument={isOpening}
                label='Incomplete Opinion Details'
                mimeType={mimeType}
                showDocumentPreview={showDocumentPreview}
                toggleDocumentPreview={toggleDocumentPreview}
                setSelectedPage={setSelectedPage}
                selectedPage={selectedPage}
                type={DocumentViewerType.INCOMPLETE_OPINION}
                showSkip={showSkip}
            >
                {opinion ? <OpinionDetails opinion={opinion} /> : <PageLoadingDots />}
            </IncompleteDetailsModal>
            {!isNull(opinion) && <OpinionConfirmationModal
                isOpen={confirmationModalOpen}
                closeModal={closeConfirmationModal}
                height={confirmationModalHeight}
                width={confirmationModalWidth}
                position={confirmationModalPosition}
                completeOpinion={completeUpload}
                opinion={opinion}
            />
            }
        </>
    );
};
